import { memo, useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { Text } from '@mantine/core';
import type { MapRef } from '@vis.gl/react-maplibre';
import { useTranslation } from 'react-i18next';
import { MAP_SETUP_ZOOM_INDICATOR_DECIMALS } from '@/constants';
import { FONT_WEIGHT_SEMIBOLD } from '@/constants/ui';
import styles from './ZoomReadout.module.css';

interface ZoomReadoutProps {
  /** Ref to the preview map instance. */
  mapRef: RefObject<MapRef | null>;
  /** Whether the map has finished loading (listeners can attach). */
  isMapReady: boolean;
  /** Configured minimum zoom, shown as context. */
  minZoom: number;
  /** Configured maximum zoom, shown as context. */
  maxZoom: number;
}

/**
 * Live current-zoom readout for the configurator zoom pill (current zoom + the
 * configured min/max as context). A memoized leaf owning its own high-frequency
 * `'zoom'` subscription, so the per-tick `setZoom` re-renders only this text —
 * not the sibling zoom buttons or the pill's panel-offset calc. Renders `null`
 * (including its leading separator) until the first zoom read.
 */
export const ZoomReadout = memo(function ZoomReadout({
  mapRef,
  isMapReady,
  minZoom,
  maxZoom,
}: ZoomReadoutProps) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState<number | null>(null);

  // Track the map's actual zoom (mouse wheel, +/- buttons, slider-driven eases).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const updateZoom = (): void => setZoom(map.getZoom());
    updateZoom();
    map.on('zoom', updateZoom);
    return () => {
      map.off('zoom', updateZoom);
    };
  }, [mapRef, isMapReady]);

  if (zoom === null) return null;

  return (
    <>
      <span className={styles.separator} aria-hidden="true" />
      <Text size="xs" fw={FONT_WEIGHT_SEMIBOLD} className={styles.readout}>
        {t('mapSetup.zoom.indicator', {
          zoom: zoom.toFixed(MAP_SETUP_ZOOM_INDICATOR_DECIMALS),
          min: minZoom.toFixed(MAP_SETUP_ZOOM_INDICATOR_DECIMALS),
          max: maxZoom.toFixed(MAP_SETUP_ZOOM_INDICATOR_DECIMALS),
        })}
      </Text>
    </>
  );
});
